// The boat's operator token, in the boat's Settings (TOM-294, cloud ADR 0420;
// contract §5.27). The boat requires it on its own network for the controls
// that change its mode and for its operator pages. Every owner may show it,
// for a local tool, and rotate it.
//
// The token is fetched only when an owner asks to see it, held in this
// component's state while shown, and dropped on Hide or after a rotation.
// While the boat has not taken the newest token, the section asks again
// every 5 s.
//
// `OperatorTokenPanelView` draws from props alone, so cloud/web's Node vitest
// renders it to static markup; `OperatorTokenPanel` owns the requests.

import { useCallback, useEffect, useState } from 'react';

import {
  operatorTokenErrorMessage,
  revealedTokenIsCurrent,
  operatorTokenStatusLine,
  operatorTokenPollDelayMs,
  parseOperatorTokenView,
  shouldPollOperatorToken,
  type OperatorTokenView,
} from '../../lib/operatorToken';

type Busy = 'reveal' | 'rotate' | null;

export interface OperatorTokenPanelViewProps {
  boatName: string;
  view: OperatorTokenView | null;
  loadFailed: boolean;
  /** The token, only while an owner has asked to see it. */
  token: string | null;
  confirmingRotate: boolean;
  copied: boolean;
  busy: Busy;
  error: string | null;
  onReveal: () => void;
  onHide: () => void;
  onCopy: () => void;
  onRotate: () => void;
  onConfirmRotate: () => void;
  onCancelRotate: () => void;
  onRetry: () => void;
}

export function OperatorTokenPanelView(props: OperatorTokenPanelViewProps): React.ReactElement {
  const { boatName, view, busy, token } = props;
  const status = view !== null ? operatorTokenStatusLine(view, boatName) : null;
  const hasToken = view?.configured === true;

  return (
    <div className="panel" data-testid="boat-settings-operator-token">
      <h3 id="boat-settings-operator-token-heading">Operator token</h3>
      <p className="muted">
        On its own network, {boatName} asks for this token before anyone changes its mode or opens
        its operator pages. Send it in the <code>x-neomaris-core-admin-token</code> header. Only
        where a tool cannot set a header, open <code>/admin?token=</code> followed by the token: a
        token in a link can stay in browser history and logs. Rotating it makes a new one, and the
        old one stops working once the boat takes the new one.
      </p>

      {props.loadFailed ? (
        <p className="alarm-form-err" role="alert" data-testid="operator-token-load-error">
          Couldn&apos;t load the operator token.{' '}
          <button type="button" className="btn" onClick={props.onRetry}>
            Try again
          </button>
        </p>
      ) : null}

      {status !== null ? (
        <p className="operator-token__status" role="status" data-testid="operator-token-status">
          {status}
        </p>
      ) : null}

      {token !== null ? (
        <div className="operator-token__value">
          <code data-testid="operator-token-value">{token}</code>
          <button
            type="button"
            className="btn"
            onClick={props.onCopy}
            data-testid="operator-token-copy"
          >
            {props.copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      ) : null}

      {hasToken ? (
        <div className="operator-token__actions">
          {token === null ? (
            <button
              type="button"
              className="btn"
              disabled={busy !== null}
              onClick={props.onReveal}
              data-testid="operator-token-reveal"
            >
              {busy === 'reveal' ? 'Loading…' : 'Show token'}
            </button>
          ) : (
            <button
              type="button"
              className="btn"
              onClick={props.onHide}
              data-testid="operator-token-hide"
            >
              Hide token
            </button>
          )}
          {props.confirmingRotate ? (
            <>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy !== null}
                onClick={props.onConfirmRotate}
                data-testid="operator-token-rotate-confirm"
              >
                {busy === 'rotate' ? 'Rotating…' : 'Rotate now'}
              </button>
              <button
                type="button"
                className="btn"
                disabled={busy !== null}
                onClick={props.onCancelRotate}
                data-testid="operator-token-rotate-cancel"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn"
              disabled={busy !== null}
              onClick={props.onRotate}
              data-testid="operator-token-rotate"
            >
              Rotate token
            </button>
          )}
        </div>
      ) : null}

      {props.confirmingRotate ? (
        <p className="muted operator-token__note" data-testid="operator-token-rotate-warning">
          Tools aboard that use the current token stop working once {boatName} takes the new one.
        </p>
      ) : null}

      {props.error !== null ? (
        <p
          className="alarm-form-err operator-token__note"
          role="alert"
          data-testid="operator-token-error"
        >
          {props.error}
        </p>
      ) : null}
    </div>
  );
}

const COPY_FAILED = 'Couldn’t copy here. The token is selected: copy it with your keyboard.';

/** Selects the shown token's text, the fallback when there is no clipboard. */
function selectTokenText(): void {
  const el = document.querySelector('[data-testid="operator-token-value"]');
  const selection = window.getSelection();
  if (el === null || selection === null) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  selection.removeAllRanges();
  selection.addRange(range);
}

async function call(boatId: string, method: 'GET' | 'POST', path = ''): Promise<unknown> {
  const res = await fetch(`/v1/boats/${encodeURIComponent(boatId)}/operator-token${path}`, {
    method,
    credentials: 'same-origin',
    cache: 'no-store',
  });
  const json: unknown = await res.json().catch(() => null);
  if (!res.ok) throw new Error(operatorTokenErrorMessage(res.status, json));
  return json;
}

async function readView(boatId: string, method: 'GET' | 'POST', path = '') {
  const view = parseOperatorTokenView(await call(boatId, method, path));
  if (view === null) throw new Error(operatorTokenErrorMessage(500, null));
  return view;
}

export function OperatorTokenPanel({
  boatId,
  boatName,
}: {
  boatId: string;
  boatName: string;
}): React.ReactElement {
  const [view, setView] = useState<OperatorTokenView | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  // The version the shown token is: a view that says another version (a
  // rotation elsewhere, or a repair) hides it, so the page never shows a
  // token the boat no longer takes.
  const [tokenVersion, setTokenVersion] = useState<number | null>(null);
  useEffect(() => {
    if (token !== null && view !== null && !revealedTokenIsCurrent(view, tokenVersion)) {
      setToken(null);
      setTokenVersion(null);
    }
  }, [view, token, tokenVersion]);
  const [confirmingRotate, setConfirmingRotate] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await readView(boatId, 'GET'));
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
  }, [boatId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Backs off and stops (operatorTokenPollDelayMs), and never asks while the
  // tab is hidden. A rotation or the tab coming back starts the asks over.
  const polling = shouldPollOperatorToken(view);
  const [asked, setAsked] = useState(0);
  const [hidden, setHidden] = useState(
    () => typeof document !== 'undefined' && document.visibilityState === 'hidden'
  );
  useEffect(() => {
    const onVisibility = () => {
      const nowHidden = document.visibilityState === 'hidden';
      setHidden(nowHidden);
      if (!nowHidden) setAsked(0);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);
  useEffect(() => {
    if (!polling || hidden) return;
    const delay = operatorTokenPollDelayMs(asked);
    if (delay === null) return;
    const id = window.setTimeout(() => {
      void load().finally(() => setAsked((n) => n + 1));
    }, delay);
    return () => clearTimeout(id);
  }, [polling, hidden, asked, load]);

  async function reveal() {
    setBusy('reveal');
    setError(null);
    try {
      const body = (await call(boatId, 'POST', '/reveal')) as {
        token?: unknown;
        version?: unknown;
      };
      if (typeof body.token !== 'string') throw new Error(operatorTokenErrorMessage(500, null));
      // Read the view first, then show the token: the stale-token check
      // compares the view's version with the reveal's, so a view read before
      // the reveal (and older than it) would hide a token just revealed.
      await load();
      setToken(body.token);
      setTokenVersion(typeof body.version === 'number' ? body.version : null);
      setCopied(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : operatorTokenErrorMessage(500, null));
    } finally {
      setBusy(null);
    }
  }

  async function rotate() {
    setBusy('rotate');
    setError(null);
    try {
      setView(await readView(boatId, 'POST', '/rotate'));
      setAsked(0);
      setToken(null);
      setConfirmingRotate(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : operatorTokenErrorMessage(500, null));
    } finally {
      setBusy(null);
    }
  }

  return (
    <OperatorTokenPanelView
      boatName={boatName}
      view={view}
      loadFailed={loadFailed}
      token={token}
      confirmingRotate={confirmingRotate}
      copied={copied}
      busy={busy}
      error={error}
      onReveal={() => void reveal()}
      onHide={() => {
        setToken(null);
        setCopied(false);
      }}
      onCopy={() => {
        if (token === null) return;
        // No clipboard (an http page, an old browser): say so, and select the
        // token so it is one keystroke away.
        const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
        if (clipboard === undefined) {
          selectTokenText();
          setError(COPY_FAILED);
          return;
        }
        clipboard
          .writeText(token)
          .then(() => setCopied(true))
          .catch(() => {
            selectTokenText();
            setError(COPY_FAILED);
          });
      }}
      onRotate={() => setConfirmingRotate(true)}
      onConfirmRotate={() => void rotate()}
      onCancelRotate={() => setConfirmingRotate(false)}
      onRetry={() => void load()}
    />
  );
}
